import { Plane, Clock } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Route } from "@/lib/types";

interface RouteCardProps {
  route: Route;
  index: number;
}

export default function RouteCard({ route, index }: RouteCardProps) {
  const formattedDate = (() => {
    try {
      // Handle both "2026-03-14" and "2026-03-14T00:00:00.000Z" formats
      // Use substring to get just YYYY-MM-DD, then parse as local date
      // (parseISO creates UTC midnight which shifts to previous day in western timezones)
      const dateStr = route.departure_date.substring(0, 10);
      const [year, month, day] = dateStr.split("-").map(Number);
      return format(new Date(year, month - 1, day), "MMMM dd, yyyy");
    } catch {
      return route.departure_date;
    }
  })();

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Plane className="h-4 w-4 text-primary" />
          <span className="font-semibold">
            Route {index + 1}: {route.origin} &rarr; {route.destination}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{formattedDate}</p>
        <div className="flex flex-wrap gap-2">
          {(!route.time_constraint || route.time_constraint.type === "any") && (
            <Badge variant="secondary">Any Time</Badge>
          )}
          {route.time_constraint?.type === "after" && route.time_constraint.time && (
            <Badge className="bg-sky-50 text-sky-700 border-sky-200">
              <Clock className="h-3 w-3 mr-1" /> Depart After {route.time_constraint.time}
            </Badge>
          )}
          {route.time_constraint?.type === "before" && route.time_constraint.time && (
            <Badge className="bg-sky-50 text-sky-700 border-sky-200">
              <Clock className="h-3 w-3 mr-1" /> Arrive Before {route.time_constraint.time}
            </Badge>
          )}
          {route.earliest_departure && (
            <Badge variant="secondary">Earliest: {route.earliest_departure}</Badge>
          )}
          {route.latest_arrival && (
            <Badge variant="secondary">Latest: {route.latest_arrival}</Badge>
          )}
          {route.direct_flights_only && (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Direct Only</Badge>
          )}
          {route.preferred_airlines && route.preferred_airlines.length > 0 &&
            route.preferred_airlines.map((airline) => (
              <Badge key={airline} variant="outline">{airline}</Badge>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}
